'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useLocale } from 'next-intl';
import { extractFaUserId } from '@utils/auth/extractFaUserId';
import { gtag } from '@utils/analytics/gtag';
import { UserProperties } from './analytics.types';

// Sets GA4 user properties (`sso_id`, `user_type`, `user_language`) via
// `gtag('set', 'user_properties', { … })` whenever the session or locale
// changes. The values survive across page views in the same browser
// session and are automatically attached to every subsequent GA4 event
// by GTM's GA4 tags — no per-tag configuration needed in the container.
//
// Does not block rendering — children render immediately; the
// user_properties push fires from useEffect after mount.
//
// Skips while next-auth's session is `loading` so we don't push an
// incorrect `not_logged` for the half-second between mount and the
// session resolving.
//
// `lastPushedRef` deduplicates: re-runs of the effect with identical
// payload values don't re-push.
export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { data: session, status } = useSession();
  const locale = useLocale();
  const lastPushedRef = useRef<string | null>(null);

  useEffect(() => {
    if (status === 'loading') return;

    const props: UserProperties = {
      sso_id: extractFaUserId(session) ?? '',
      user_type: status === 'authenticated' ? 'logged' : 'not_logged',
      user_language: locale,
    };

    const serialised = JSON.stringify(props);
    if (lastPushedRef.current === serialised) return;
    lastPushedRef.current = serialised;

    gtag('set', 'user_properties', props);
  }, [session, status, locale]);

  return <>{children}</>;
};
